import { useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import SiteFooter from "@/components/SiteFooter";

/**
 * Shared layout for public legal/contact pages.
 * NOTE for editors: the text content on these pages is a DRAFT. Have a lawyer
 * review before relying on it commercially. Kept intentionally calm + readable
 * on dark theme.
 *
 * `lang` flips a small amount of chrome (nav labels, eyebrow, last-updated
 * label, footer lang, html-lang) so the same component is used for both
 * Turkish (/kullanim-kosullari …) and English (/en/terms-of-service …) pages
 * without duplicating layout.
 */
const COPY = {
  tr: {
    eyebrow: "Yasal",
    lastUpdated: "Son güncelleme",
    version: "Versiyon",
    draftNotice: "Bu hukuki metin bir taslaktır ve hukuk profesyoneli tarafından gözden geçirilmesi önerilir.",
    navPricing: "Fiyatlandırma",
    navLogin: "Giriş Yap",
    navStart: "Ücretsiz Başla",
    homeHref: "/tr",
  },
  en: {
    eyebrow: "Legal",
    lastUpdated: "Last updated",
    version: "Version",
    draftNotice: "This is a draft legal text and should be reviewed by a legal professional.",
    navPricing: "Pricing",
    navLogin: "Log in",
    navStart: "Start free",
    homeHref: "/",
  },
};

export default function LegalPage({ title, subtitle, lastUpdated, version, children, testid, lang = "tr" }) {
  const t = COPY[lang] || COPY.tr;

  useEffect(() => {
    const prev = document.documentElement.lang;
    document.documentElement.lang = lang;
    return () => { document.documentElement.lang = prev; };
  }, [lang]);

  return (
    <div className="min-h-screen bg-[#0a0a14] text-slate-200 flex flex-col">
      <div className="absolute inset-0 bg-grid opacity-[0.08] pointer-events-none" />

      <header className="relative z-10 px-6 lg:px-12 py-6 flex items-center justify-between">
        <Link to={t.homeHref} className="inline-flex items-center"><Logo /></Link>
        <nav className="flex items-center gap-5 text-sm">
          <Link to="/fiyatlandirma" className="text-slate-300 hover:text-cyan-300">{t.navPricing}</Link>
          <Link to="/giris" className="text-slate-300 hover:text-cyan-300">{t.navLogin}</Link>
          <Link to="/kayit" className="neon-btn rounded-lg px-4 py-2 text-white text-sm font-medium">{t.navStart}</Link>
        </nav>
      </header>

      <main className="relative z-10 flex-1 px-6 lg:px-12 py-12">
        <div className="max-w-3xl mx-auto" data-testid={testid}>
          <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-300 mb-3">{t.eyebrow}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight">{title}</h1>
          {subtitle && <p className="text-slate-300 text-lg leading-relaxed mb-2">{subtitle}</p>}
          {lastUpdated && <div className="text-xs text-slate-400 mb-1">{t.lastUpdated}: {lastUpdated}</div>}
          {version && <div className="text-[11px] text-slate-500 mb-6" data-testid="legal-version">{t.version}: {version}</div>}
          {!version && lastUpdated && <div className="mb-6" />}

          <div className="mb-8 rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-4 py-3 text-xs text-amber-200 leading-relaxed" data-testid="legal-draft-notice">
            {t.draftNotice}
          </div>

          <article className="prose prose-invert max-w-none legal-body">
            {children}
          </article>
        </div>
      </main>

      <SiteFooter lang={lang} />

      <style>{`
        .legal-body { color: rgb(203, 213, 225); line-height: 1.75; font-size: 0.975rem; }
        .legal-body h2 { color: white; font-family: var(--font-display, inherit); font-weight: 700; font-size: 1.4rem; margin-top: 2.5rem; margin-bottom: 0.9rem; letter-spacing: -0.01em; }
        .legal-body h3 { color: rgb(226, 232, 240); font-weight: 600; font-size: 1.05rem; margin-top: 1.6rem; margin-bottom: 0.5rem; }
        .legal-body p { margin: 0.9rem 0; }
        .legal-body ul { list-style: disc; padding-left: 1.4rem; margin: 0.8rem 0; }
        .legal-body li { margin: 0.35rem 0; }
        .legal-body strong { color: white; }
        .legal-body a { color: rgb(103, 232, 249); text-decoration: underline; text-underline-offset: 3px; }
        .legal-body a:hover { color: rgb(165, 243, 252); }
      `}</style>
    </div>
  );
}
